import { useEffect, useState } from 'react';
import { desktopAccountRequest } from './desktopAccountBridge';
import { AccountSelect } from './AccountSelect';

export type Appearance = {
  theme: 'light' | 'dark' | 'system';
  density: 'compact' | 'comfortable' | 'spacious';
  uiSize: string;
  documentSize: string;
  reducedMotion: string;
  editorStyle: 'marvin';
};
export const defaultAppearance: Appearance = { theme: 'dark', density: 'comfortable', uiSize: '14', documentSize: '17', reducedMotion: 'false', editorStyle: 'marvin' };
export function storedAppearance(): Appearance {
  try {
    const saved = JSON.parse(localStorage.getItem('marvin.workbench.appearance.v1') || '{}');
    return {
      theme: ['light', 'dark', 'system'].includes(saved.theme) ? saved.theme : 'dark',
      density: ['compact', 'comfortable', 'spacious'].includes(saved.density) ? saved.density : 'comfortable',
      uiSize: ['13', '14', '16', '18'].includes(saved.uiSize) ? saved.uiSize : '14',
      documentSize: ['16', '17', '18', '20'].includes(saved.documentSize) ? saved.documentSize : '17',
      reducedMotion: saved.reducedMotion === 'true' ? 'true' : 'false',
      editorStyle: 'marvin',
    };
  } catch { return defaultAppearance; }
}
export function applyAppearance(value: Appearance, dark?: boolean) {
  const root = document.documentElement;
  root.dataset.theme = (dark ?? (value.theme === 'dark' || (value.theme === 'system' && matchMedia('(prefers-color-scheme: dark)').matches))) ? 'dark' : 'light';
  root.dataset.density = value.density;
  root.dataset.reducedMotion = value.reducedMotion;
  root.style.setProperty('--ui-size', `${value.uiSize}px`);
  root.style.setProperty('--doc-size', `${value.documentSize}px`);
}

export function AppearanceSettings({ initial, hosted = false }: { initial: Appearance; hosted?: boolean }) {
  const [value, setValue] = useState<Appearance>({ ...initial, editorStyle: 'marvin' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setValue({ ...initial, editorStyle: 'marvin' }); }, [initial]);
  async function change(key: keyof Appearance, next: string) {
    setBusy(true); setError('');
    const updated = { ...value, [key]: next, editorStyle: 'marvin' as const };
    try {
      if (hosted) {
        const result = await desktopAccountRequest<{ appearance: Appearance; dark: boolean; warning?: string }>('appearance', { [key]: next });
        const appearance: Appearance = { ...result.appearance, editorStyle: 'marvin' };
        setValue(appearance); applyAppearance(appearance, result.dark);
        if (result.warning) setError(result.warning);
      } else {
        setValue(updated); applyAppearance(updated);
        localStorage.setItem('marvin.workbench.appearance.v1', JSON.stringify(updated));
        window.dispatchEvent(new Event('marvin-appearance'));
      }
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  const choices = [
    ['theme', 'Colour theme', [['system', 'Follow system'], ['light', 'Light'], ['dark', 'Dark']]],
    ['uiSize', 'Interface text', [['13', 'Small · 13'], ['14', 'Default · 14'], ['16', 'Large · 16'], ['18', 'Extra large · 18']]],
    ['documentSize', 'Document text', [['16', '16'], ['17', 'Default · 17'], ['18', '18'], ['20', '20']]],
    ['density', 'Spacing', [['compact', 'Compact'], ['comfortable', 'Comfortable'], ['spacious', 'Spacious']]],
  ] as const;
  return <section className="appearance-settings">
    <p className="account-hint">Choose what is comfortable for you. Saved on this device.</p>
    <fieldset disabled={busy}>
      {choices.map(([key, label, options]) => <label className="appearance-row" key={key} htmlFor={`appearance-${key}`}>
        <span>{label}</span><AccountSelect hosted={hosted} disabled={busy} aria-label={label} id={`appearance-${key}`} value={value[key]} onValueChange={next => void change(key, next)}>
          {options.map(([v, text]) => <option key={v} value={v}>{text}</option>)}
        </AccountSelect>
      </label>)}
      <label className="appearance-row" htmlFor="appearance-motion"><span>Reduce motion</span>
        <input id="appearance-motion" type="checkbox" checked={value.reducedMotion === 'true'} onChange={(e) => void change('reducedMotion', String(e.target.checked))} />
      </label>
    </fieldset>
    <p className="account-hint">Document text changes your reading view. Your saved formatting and exported work stay the same.</p>
    {error && <p role="alert">{error}</p>}
  </section>;
}
